/**
 * Work Hours Panel (Schritt 3)
 *
 * Arbeitstage und Arbeitszeiten sind eine ACCOUNT-Einstellung (nicht pro
 * Kampagne): der Step-Runner liest workDays/workHours/timezone vom
 * LinkedInAccount. Bei mehreren verbundenen Accounts wählt der User hier den
 * Account und konfiguriert dessen Zeitfenster — es gilt sofort für alle
 * Kampagnen dieses Accounts.
 *
 * Zeitfenster folgen denselben Regeln wie in der Kontoverwaltung (work-blocks.ts):
 * höchstens zwei Fenster pro Tag, je 1–3 Stunden, zusammen höchstens 5 Stunden.
 * Das frühere freie Von-bis-Feld schlug 09–17 Uhr vor — acht Stunden am Stück, die der
 * Server immer abgelehnt hat.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField, MenuItem, CircularProgress } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { getAllLinkedinAccounts } from '../../../API/linkedin-accounts';
import { patchLinkedinAccountWorktime } from '../../../API/linkedin-account-limits';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';
import WorkHoursSelect from '../../co-pilot/campaign-builder/campaign-building-panel/campaign-settings/work-hours-select';
import {
  formatBlockRange,
  normalizeWorkHours,
  toggleWorkHour,
  toWorkBlocks,
  validateWorkHours,
} from '../../co-pilot/campaign-builder/campaign-building-panel/campaign-settings/work-blocks';

interface WorkHoursPanelProps {
  /** Nach erfolgreichem Speichern, mit Kurzbeschreibung für den Chat. */
  onSaved: (summary: string) => void;
  /** Alle Accounts haben Arbeitszeiten — der Wizard geht zum nächsten Schritt. */
  onComplete?: () => void;
}

interface AccountRow {
  id: string;
  label: string;
  workDays: string[];
  workHours: number[];
  timezone: string;
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DAY_LABELS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const DAY_LABELS_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Vorschlag für ein noch nicht eingerichtetes Konto: 9–12 und 14–16 Uhr (5 Stunden). */
const DEFAULT_WORK_HOURS = [9, 10, 11, 14, 15];

const WorkHoursPanel: React.FC<WorkHoursPanelProps> = ({ onSaved, onComplete }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const dayLabels = isDe ? DAY_LABELS_DE : DAY_LABELS_EN;

  const dataVersion = useAIChatStore((state) => state.dataVersion);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountId, setAccountId] = useState('');
  const [days, setDays] = useState<boolean[]>([true, true, true, true, true, false, false]);
  const [workHours, setWorkHours] = useState<number[]>(DEFAULT_WORK_HOURS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getAllLinkedinAccounts();
        if (cancelled) return;
        const rows: AccountRow[] = (res?.allLinkedInAccounts || []).map((a: any) => ({
          id: a._id,
          label:
            [a.firstName, a.lastName].filter(Boolean).join(' ') ||
            a.linkedinEmail ||
            a._id,
          workDays: a.workDays || [],
          workHours: a.workHours || [],
          timezone: a.timezone || 'Europe/Berlin',
        }));
        setAccounts(rows);
        if (rows.length > 0) selectAccount(rows[0]);
      } catch {
        if (!cancelled) setError(isDe ? 'Accounts konnten nicht geladen werden.' : 'Could not load accounts.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Neu laden, wenn der Bot Daten geändert haben könnte (nach jedem Chat-Turn).
  }, [dataVersion]);

  const selectAccount = (row: AccountRow) => {
    setAccountId(row.id);
    setDays(DAY_KEYS.map((d) => (row.workDays.length > 0 ? row.workDays.includes(d) : d !== 'saturday' && d !== 'sunday')));
    // Alte, frei gewählte Stundenlisten auf die gültigen Fenster kürzen, statt sie unspeicherbar anzuzeigen.
    const stored = normalizeWorkHours(row.workHours);
    setWorkHours(stored.length > 0 ? stored : DEFAULT_WORK_HOURS);
    setSaved(false);
  };

  const currentAccount = accounts.find((a) => a.id === accountId);
  const accountConfigured = (a: AccountRow) => a.workDays.length > 0 && a.workHours.length > 0;
  const unconfigured = useMemo(() => accounts.filter((a) => !accountConfigured(a)), [accounts]);

  const validHours = workHours.length > 0 && validateWorkHours(workHours).valid;
  const canSave = !!accountId && days.some(Boolean) && validHours && !saving;

  const handleSave = async () => {
    if (!canSave || !currentAccount) return;
    setSaving(true);
    setError(null);
    try {
      const workDays = DAY_KEYS.filter((_, i) => days[i]);
      const res: any = await patchLinkedinAccountWorktime(accountId, {
        workDays,
        workHours,
        timeZone: currentAccount.timezone,
      });
      if (res?.error) throw new Error('save failed');

      setAccounts((prev) =>
        prev.map((a) => (a.id === accountId ? { ...a, workDays, workHours } : a))
      );
      setSaved(true);

      const dayText = DAY_KEYS.map((d, i) => (days[i] ? dayLabels[i] : null)).filter(Boolean).join(', ');
      const windowText = toWorkBlocks(workHours).map(formatBlockRange).join(isDe ? ' und ' : ' and ');
      onSaved(
        isDe
          ? `Ich habe die Arbeitszeiten für den LinkedIn-Account „${currentAccount.label}" im Formular gespeichert: ${dayText}, ${windowText} Uhr (${currentAccount.timezone}). Bitte bestätige kurz und schlage mir die nächsten Schritte vor.`
          : `I just saved the working hours for the LinkedIn account "${currentAccount.label}" in the form: ${dayText}, ${windowText} (${currentAccount.timezone}). Please confirm briefly and suggest my next steps.`
      );

      // Weiter: zum nächsten Account ohne Arbeitszeiten, sonst zum nächsten Schritt.
      const remaining = accounts.filter((a) => a.id !== accountId && !accountConfigured(a));
      if (remaining.length > 0) selectAccount(remaining[0]);
      else onComplete?.();
    } catch {
      setError(isDe ? 'Speichern fehlgeschlagen — bitte erneut versuchen.' : 'Saving failed — please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (accounts.length === 0) {
    return (
      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {isDe
            ? 'Noch kein LinkedIn-Account verbunden — schließe zuerst Schritt 2 ab.'
            : 'No LinkedIn account connected yet — complete step 2 first.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {unconfigured.length > 0 && !saved ? (
        <Box className={classes.gapBanner}>
          <span className={classes.gapDot}>!</span>
          <Typography variant="caption" className={classes.gapText}>
            {isDe
              ? `Fehlt in diesem Schritt: Arbeitszeiten für ${unconfigured.map((a) => a.label).join(', ')}`
              : `Missing in this step: working hours for ${unconfigured.map((a) => a.label).join(', ')}`}
          </Typography>
        </Box>
      ) : (
        <Box className={cx(classes.gapBanner, classes.doneBanner)}>
          <span className={cx(classes.gapDot, classes.doneDot)}>✓</span>
          <Typography variant="caption" className={cx(classes.gapText, classes.doneText)}>
            {isDe ? 'Arbeitszeiten gespeichert' : 'Working hours saved'}
          </Typography>
        </Box>
      )}

      {accounts.length > 1 && (
        <Box className={classes.card}>
          <Typography variant="caption" className={classes.sectionLabel}>
            {isDe ? 'LinkedIn-Account' : 'LinkedIn account'}
          </Typography>
          <TextField
            size="small"
            fullWidth
            select
            value={accountId}
            onChange={(e) => {
              const row = accounts.find((a) => a.id === e.target.value);
              if (row) selectAccount(row);
            }}
            helperText={
              isDe
                ? 'Arbeitszeiten gelten pro Account — für alle seine Kampagnen.'
                : 'Working hours apply per account — to all of its campaigns.'
            }
          >
            {accounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.label} {accountConfigured(a) ? '✓' : `— ${isDe ? 'noch offen' : 'not set'}`}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {isDe
            ? 'Bestimme, zu welchen Zeiten dein SAIA für dich arbeiten soll.'
            : 'Choose when your SAIA should work for you.'}
        </Typography>
        <Typography variant="caption" className={classes.sectionLabel}>
          {isDe ? 'Arbeitstage' : 'Working days'}
        </Typography>
        <Box className={classes.daysGrid}>
          {dayLabels.map((label, i) => (
            <button
              key={label}
              type="button"
              className={cx(classes.dayButton, days[i] && 'active')}
              onClick={() => {
                setDays((prev) => prev.map((v, j) => (j === i ? !v : v)));
                setSaved(false);
              }}
            >
              {label}
            </button>
          ))}
        </Box>

        <Box className={classes.timeRow}>
          <WorkHoursSelect
            workHours={workHours}
            handleWorkHoursChange={(hour: number) => () => {
              setWorkHours((prev) => toggleWorkHour(prev, hour));
              setSaved(false);
            }}
          />
        </Box>
        <Typography variant="caption" color="textSecondary">
          {isDe
            ? `Zeitzone: ${currentAccount?.timezone || 'Europe/Berlin'} — Änderungen gelten sofort, auch für laufende Kampagnen dieses Accounts.`
            : `Timezone: ${currentAccount?.timezone || 'Europe/Berlin'} — changes apply immediately, including running campaigns of this account.`}
        </Typography>
      </Box>

      {error && (
        <Typography variant="caption" className={classes.error}>
          {error}
        </Typography>
      )}

      <Box className={classes.footer}>
        <Typography variant="caption" className={classes.footerHint}>
          {saved
            ? isDe
              ? 'Gespeichert — der Assistent meldet sich im Chat.'
              : 'Saved — the assistant will follow up in the chat.'
            : isDe
              ? 'Der Bot arbeitet nur innerhalb dieser Zeitfenster.'
              : 'The bot only works within these time windows.'}
        </Typography>
        <button type="button" className={classes.saveButton} onClick={handleSave} disabled={!canSave}>
          {saving ? (isDe ? 'Speichert…' : 'Saving…') : isDe ? 'Weiter' : 'Next'}
        </button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 620,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  daysGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: 8,
  },
  dayButton: {
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    borderRadius: 10,
    padding: '9px 0',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 600,
    color: dc.faint,
    cursor: 'pointer',
    '&.active': {
      border: `1px solid ${dc.primary}`,
      backgroundColor: '#EEF2FF',
      color: dc.primary,
    },
  },
  timeRow: {
    marginTop: 12,
    marginBottom: 8,
  },
  error: {
    color: theme.palette.error.main,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  footerHint: {
    color: dc.muted,
    lineHeight: 1.4,
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default WorkHoursPanel;
